/**
 * contentValidators/textQualityChecks.ts
 * Validates question text quality: forbidden vocabulary, jargon, sentence length,
 * question text length, and context field length.
 */

import type { ValidationIssue } from '../../issueContract';
import { coach, info } from '../../issueContract';
import { getTierBand, getForbiddenVocab, getMaxQuestionLength, getMaxContextLength } from '../../policyEngine';
import { detectJargon, detectLongSentences, getMaxWordsPerSentence } from '../../../utils';
import type { Question } from '../../../../content';
import { STAGE } from './shared';
import { SEVERITY } from '../../severityScale';

/**
 * Validate question text quality: forbidden vocab, jargon, sentence length,
 * text length, and context field length.
 */
export function validateTextQuality(question: Question): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const qId = question.id ?? 'UNKNOWN';
  const fId = question.familyId ?? 'UNKNOWN';
  const tier = question.tier ?? 1;
  const module = question.module;

  // ── Forbidden vocabulary ─────────────────────────────────────────────────

  const forbidden = getForbiddenVocab(tier);
  const questionText = (question.question ?? '').toLowerCase();
  for (const word of forbidden) {
    if (questionText.includes(word.toLowerCase())) {
      issues.push(coach('content', STAGE, 'CONTENT_FORBIDDEN_VOCAB', `Word "${word}" too complex for tier ${tier} (${getTierBand(tier)} band)`, {
        field: 'question',
        actual: word,
        questionId: qId,
        familyId: fId,
        severity: SEVERITY.NEAR_MEDIUM,
        suggestion: `Use simpler vocabulary appropriate for tier ${tier}`
      }));
    }
  }

  // ── Jargon detection in question text ────────────────────────────────────
  {
    const qJargon = detectJargon(question.question ?? '', tier);
    for (const {
      term,
      replacement
    } of qJargon) {
      issues.push(coach('content', STAGE, 'CONTENT_JARGON_DETECTED', `Jargon "${term}" in question text — too complex for children aged 9-11`, {
        field: 'question',
        actual: term,
        questionId: qId,
        familyId: fId,
        severity: SEVERITY.NEAR_MEDIUM,
        suggestion: `Replace "${term}" with ${replacement}`
      }));
    }
  }

  // ── Sentence length in question text ─────────────────────────────────────
  {
    const qLongSentences = detectLongSentences(question.question ?? '', tier);
    if (qLongSentences.length > 0) {
      const maxWords = getMaxWordsPerSentence(tier);
      issues.push(info('content', STAGE, 'CONTENT_QUESTION_SENTENCE_TOO_LONG', `${qLongSentences.length} sentence(s) in question text exceed the ${maxWords}-word limit for tier ${tier}`, {
        field: 'question',
        questionId: qId,
        familyId: fId,
        severity: SEVERITY.NEAR_MINOR,
        suggestion: `Keep question sentences ≤${maxWords} words for tier ${tier} (groep ${tier <= 4 ? 6 : tier <= 8 ? 7 : 8})`
      }));
    }
  }

  // ── Question text length ─────────────────────────────────────────────────

  const maxLength = getMaxQuestionLength(tier, module);
  if (questionText.length > maxLength) {
    issues.push(coach('content', STAGE, 'CONTENT_QUESTION_TOO_LONG', `Question text (${questionText.length} chars) exceeds max ${maxLength} for tier ${tier}`, {
      field: 'question',
      expected: `≤${maxLength}`,
      actual: String(questionText.length),
      questionId: qId,
      familyId: fId,
      severity: SEVERITY.LOW
    }));
  }

  // ── Context field length validation ──────────────────────────────────────

  const contextText = (question as Record<string, unknown>).context;
  if (contextText && typeof contextText === 'string' && contextText.length > 0) {
    const band = getTierBand(tier);
    const maxCtx = getMaxContextLength(tier);
    if (contextText.length > maxCtx) {
      issues.push(info('content', STAGE, 'CONTENT_CONTEXT_TOO_LONG', `Context field (${contextText.length} chars) exceeds recommended max ${maxCtx} for tier ${tier} (${band} band)`, {
        field: 'context',
        expected: `≤${maxCtx}`,
        actual: String(contextText.length),
        questionId: qId,
        familyId: fId,
        severity: SEVERITY.COSMETIC
      }));
    }
  }
  return issues;
}